import type { HubTableDataTableProps } from "@/ds";
import { hubTableCartao } from "../hub-table-cartao";
import styles from "./obras-lista-table.module.scss";
import {
  OBRAS_LISTA_TABLE_DETAIL_KEY,
  OBRAS_LISTA_TABLE_EMBARCACAO_KEY,
  OBRAS_LISTA_TABLE_ID_KEY,
} from "./obras-lista-table.constants";
import type {
  ObrasListaTableColumn,
  ObrasListaTableItem,
  ObrasListaTableProps,
  ObrasListaTableViewModel,
} from "./types-obras-lista-table";

/**
 * ViewModel da tabela da Lista de obras: colunas visíveis já resolvidas (mobile, densidade, largura, fixação e
 * cabeçalho), o formato de ordenação do `HubTable` e as props do `DataTable`; sem JSX.
 */
export function useObrasListaTable<
  T extends ObrasListaTableItem,
  K extends string,
  S extends string,
>({
  items,
  total,
  status,
  errorMessage,
  first,
  rows,
  rowsOptions,
  onPage,
  sort,
  onSort,
  isSortField,
  settings,
  isMobile,
  catalogo,
  larguraDaColuna,
  colunasQueCrescem,
  colunasDeTags,
  ajustarRotulo,
  renderCell,
  semValor,
  onDetail,
  loadingLabel,
  rowsPerPageLabel,
  ariaLabel,
  testId,
  variant,
  empty,
  error,
}: ObrasListaTableProps<T, K, S>): ObrasListaTableViewModel<T, K, S> {
  const density = settings.primeSize;
  // Primeira carga (sem linhas ainda): o cartão não pode colapsar no spinner e saltar quando as linhas chegam.
  const isFirstLoad = status === "loading" && items.length === 0;

  // A ordenação mora na tela (os Cards a compartilham): aqui só no formato que o HubTable espera de `useTableState`.
  const table = {
    first,
    rows,
    rowsOptions,
    sortField: sort?.field,
    sortOrder: sort ? (sort.direction === "asc" ? (1 as const) : (-1 as const)) : (0 as const),
    onPage,
    onSort: (event: Parameters<ObrasListaTableViewModel<T, K, S>["table"]["onSort"]>[0]) =>
      onSort(
        event.sortOrder && isSortField(event.sortField)
          ? { field: event.sortField, direction: event.sortOrder === -1 ? "desc" : "asc" }
          : null,
      ),
  };

  const visiveis = catalogo
    .filter((column) => settings.isColumnVisible(column.key))
    // No mobile ID + embarcação formam uma única coluna "Obra".
    .filter((column) => !(isMobile && column.key === OBRAS_LISTA_TABLE_EMBARCACAO_KEY));

  // Soma das larguras do Figma das colunas visíveis: ocultar colunas não estica as que ficam.
  const tableMinWidthRem = visiveis.reduce(
    (sum, column) => sum + Number.parseFloat(larguraDaColuna(column.key, density, isMobile)),
    0,
  );

  // Coluna de texto livre, não fixa, que cresce com a janela: a primeira da lista da tela que estiver visível.
  const growKey = colunasQueCrescem.find((key) => visiveis.some((column) => column.key === key));

  const columns = visiveis.map((column): ObrasListaTableColumn<K, S> => {
    const isDetail = column.key === OBRAS_LISTA_TABLE_DETAIL_KEY;
    const isMobileObra = isMobile && column.key === OBRAS_LISTA_TABLE_ID_KEY;
    const frozen =
      isMobileObra ||
      (!isMobile &&
        (column.key === OBRAS_LISTA_TABLE_ID_KEY ||
          column.key === OBRAS_LISTA_TABLE_EMBARCACAO_KEY));
    const width = larguraDaColuna(column.key, density, isMobile);
    // Confortável mostra o nome completo (quebra em 2 linhas); Compacto, o rótulo abreviado (a tooltip traz o completo).
    const label = isMobile && column.mobileHeader ? column.mobileHeader : column.label;
    const headerText = isMobileObra
      ? "Obra"
      : isDetail
        ? ""
        : density === "small"
          ? column.header
          : (ajustarRotulo?.(label) ?? label);

    const kind = isDetail ? "detail" : isMobileObra ? "obra" : "cell";
    const bodyClassName =
      kind === "detail"
        ? hubTableCartao.actionCellClassName
        : kind === "obra"
          ? `${styles.idCell} ${styles.obraFixed}`
          : colunasDeTags.includes(column.key)
            ? styles.tagsCell
            : column.key === OBRAS_LISTA_TABLE_ID_KEY
              ? styles.idCell
              : undefined;

    return {
      key: column.key,
      field: column.sortField,
      headerText,
      headerTooltip: isMobileObra || isDetail ? undefined : column.tooltip,
      sortable: column.sortField !== undefined,
      frozen: frozen || (isDetail && !isMobile),
      alignFrozen: isDetail ? "right" : undefined,
      align: isDetail ? "center" : column.align,
      alignHeader: isDetail ? "center" : column.align,
      style: column.key === growKey ? { minWidth: width } : { width, minWidth: width },
      headerClassName:
        kind === "obra"
          ? styles.obraFixed
          : kind === "detail"
            ? hubTableCartao.actionCellClassName
            : undefined,
      bodyClassName,
      kind,
    };
  });

  // Layout fixo: as larguras do Figma não dependem do conteúdo. Com layout automático o ID e o Término cresciam com
  // o dado (QA-2040-06) e o `left` da coluna fixa, que o Prime mede uma vez, ficava velho quando um filtro trocava
  // as linhas (gap ou sobreposição entre as colunas fixas). Em janela larga só a coluna `growKey` (sem largura,
  // com o mínimo do Figma) absorve a sobra: as fixas e as demais seguem com a largura exata.
  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    // A rolagem é do corpo da tabela (cabeçalho fixo): o cartão cresce com as linhas até a altura da janela (tela).
    // O `flex` liga o layout em coluna do Prime; o `height: 100%` dele é neutralizado em `.table` e `.tableScroller`.
    scrollHeight: "flex",
    tableStyle: {
      tableLayout: "fixed",
      width: `max(100%, ${tableMinWidthRem}rem)`,
      minWidth: `${tableMinWidthRem}rem`,
    },
    "aria-label": ariaLabel,
  };

  return {
    items,
    total,
    status,
    errorMessage,
    settings,
    renderCell,
    semValor,
    onDetail,
    loadingLabel,
    rowsPerPageLabel,
    testId,
    wrapperTestId: `${testId}-table-wrapper`,
    obraCellTestId: `${testId}-obra-cell`,
    detailTestId: (item: T) => `${testId}-detail-${item.id}`,
    variant: variant ?? "padrao",
    empty,
    error,
    table,
    columns,
    compact: density === "small",
    isFirstLoad,
    dataTableProps,
  };
}
